import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CContainer, CSpinner, CButton } from '@coreui/react';
import api from '../api/axiosInstance';

// Core KaTeX Engine Setup
import 'katex/dist/katex.min.css';
import katex from 'katex';

// HTML to PDF Engine Core Import
import html2pdf from 'html2pdf.js';

const DownloadPDF = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const pdfTemplateRef = useRef(null);

  const [questionData, setQuestionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchQuestionDetails = async () => {
      try {
        setLoading(true);
        // Apne target core single question controller parameter endpoints call karein
        const res = await api.get(`/api/Content/GetQuestionDetails/${id}`);
        setQuestionData(res.data);
      } catch (err) {
        console.error("Error fetching question data for PDF:", err);
        alert("Failed to load question details.");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchQuestionDetails();
    }
  }, [id, navigate]);

  // Execute actual automatic pdf render transformation layers trigger
  useEffect(() => {
    if (!loading && questionData && pdfTemplateRef.current) {
      triggerPDFDownload();
    }
  }, [loading, questionData]);

  const triggerPDFDownload = () => {
    setDownloading(true);
    const element = pdfTemplateRef.current;
    
    // PDF Document Structural Configuration Options Blueprint
    const options = {
      margin:       [15, 15, 15, 15], // Perfect border padding spacing alignment
      filename:     `Question_${questionData.customQuestionId || id}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true, logging: false }, // Math expressions sharpness high precision scale 2
      jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' } // A4 Paper standard format layout
    };

    html2pdf()
      .from(element)
      .set(options)
      .save()
      .then(() => {
        setDownloading(false);
        // Download complete hone ke baad automatic user ko pichle main question screen par wapas bhej dega
        navigate(-1); 
      })
      .catch((err) => {
        console.error("PDF generation layout processing error:", err);
        setDownloading(false);
      });
  };

  // Robust Mathematical LaTeX Equation Grid Layout System Parser
  const renderMathText = (rawText) => {
    if (!rawText) return '';
    try {
      const lines = rawText.split(/\\\\/);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {lines.map((lineText, lineIdx) => {
            if (!lineText.trim()) return null;

            let formattedLine = lineText.replace(/\\\s/g, ' ').replace(/\\frac13/g, '\\frac{1}{3}').trim();

            if (formattedLine.includes('\\text{')) {
              const textMatch = formattedLine.match(/\\text\{([\s\S]*?)\}/);
              const textContent = textMatch ? textMatch[1] : '';
              let mathSegment = formattedLine.replace(/\\text\{[\s\S]*?\}/, '').trim();
              mathSegment = mathSegment.replace(/^\\+\s*/, '\\');

              let compiledMathHTML = '';
              if (mathSegment) {
                try {
                  compiledMathHTML = katex.renderToString(mathSegment, { displayMode: false, throwOnError: false });
                } catch { compiledMathHTML = mathSegment; }
              }

              return (
                <div key={lineIdx} style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                  <span style={{ whiteSpace: 'pre-wrap', marginRight: '6px', color: '#212529' }}>{textContent}</span>
                  {compiledMathHTML && <span dangerouslySetInnerHTML={{ __html: compiledMathHTML }} style={{ display: 'inline-block', verticalAlign: 'middle' }} />}
                </div>
              );
            }

            if (formattedLine.includes('\\') || formattedLine.includes('^') || formattedLine.includes('_') || formattedLine.includes('|')) {
              try {
                const html = katex.renderToString(formattedLine, { displayMode: false, throwOnError: false });
                return (
                  <div key={lineIdx}>
                    <span dangerouslySetInnerHTML={{ __html: html }} style={{ display: 'inline-block', verticalAlign: 'middle', fontSize: '1.1rem' }} />
                  </div>
                );
              } catch {
                return <div key={lineIdx} style={{ fontSize: '1.1rem' }}>{formattedLine}</div>;
              }
            }

            return <div key={lineIdx} style={{ whiteSpace: 'pre-wrap', fontSize: '1.1rem', color: '#212529' }}>{formattedLine}</div>;
          })}
        </div>
      );
    } catch { return <div>{rawText}</div>; }
  };

  if (loading || downloading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '80vh' }}>
        <CSpinner color="danger" size="lg" className="mb-3" />
        <h5 className="text-secondary fw-bold">Preparing Your PDF Document Download...</h5>
        <p className="text-muted small">Converting KaTeX layouts and packaging asset data buffers.</p>
      </div>
    );
  }

  return (
    <div>
      {/* 🌟 USER INVISIBLE TEMPLATE REF CONTAINER AREA FOR CAPTURING CLEAN PRINT ENVIRONMENT 🌟 */}
      <div style={{ position: 'absolute', left: '-9999px', top: '0' }}>
        <div ref={pdfTemplateRef} style={{ width: '180mm', padding: '10px', fontFamily: 'Arial, sans-serif', color: '#000' }}>
          
          {/* Document Header Branding */}
          <div style={{ borderBottom: '2px solid #5856d6', paddingBottom: '10px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: '0', color: '#5856d6', fontSize: '1.6rem', fontWeight: 'bold' }}>AMBIPIE LEARNING PORTAL</h2>
              <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>Verified Academic Core Syllabus Resource Repository</span>
            </div>
            <div style={{ textAlign: 'right', fontSize: '0.85rem', color: '#495057' }}>
              <div><b>Question ID:</b> {questionData.customQuestionId || `NSRN01000${id}`}</div>
              <div><b>Type:</b> {questionData.questionType || 'SingleCorrect'}</div>
            </div>
          </div>

          {/* Academic Topic Track Breadcrumb Info Row */}
       {/* Academic Topic Track Breadcrumb Info Row */}
<table style={{ width: '100%', marginBottom: '25px', fontSize: '0.85rem', borderCollapse: 'collapse', backgroundColor: '#f8f9fa' }}>
  <tbody>
    <tr>
      <td style={{ padding: '8px', border: '1px solid #dee2e6', width: '50%' }}>
        <b>Chapter:</b> {questionData.chapterName || 'N/A'}
      </td>
      <td style={{ padding: '8px', border: '1px solid #dee2e6', width: '50%' }}>
        <b>Topic:</b> {questionData.topicName || 'N/A'}
      </td>
    </tr>
    <tr>
      {/* 🌟 UPDATED: AskedIn me backend se exam name + year dono combine hokar aa rahe hain 🌟 */}
      <td style={{ padding: '8px', border: '1px solid #dee2e6', width: '50%' }}>
        <b>Exam Reference:</b> {questionData.askedIn || 'N/A'}-{questionData.askedYear|| 'N/A'}
      </td>
      <td style={{ padding: '8px', border: '1px solid #dee2e6', width: '50%' }}>
        <b>Book Source:</b> {questionData.bookReference || 'N/A'}
      </td>
    </tr>
  </tbody>
</table>

          {/* The Question Text Section Box Container */}
          <div style={{ marginBottom: '25px' }}>
            <h4 style={{ color: '#5856d6', marginBottom: '10px', fontSize: '1.15rem', borderBottom: '1px dashed #dee2e6', paddingBottom: '5px' }}>QUESTION STATEMENT:</h4>
            <div style={{ padding: '12px', backgroundColor: '#fff', borderLeft: '4px solid #5856d6', lineHeight: '1.6' }}>
              {renderMathText(questionData.questionText)}
            </div>
          </div>

          {/* Options Structural List Blocks Layout */}
          <div style={{ marginBottom: '30px' }}>
            <h4 style={{ color: '#495057', marginBottom: '12px', fontSize: '1.15rem' }}>OPTIONS:</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {['optionA', 'optionB', 'optionC', 'optionD'].map((key, index) => {
                const label = String.fromCharCode(65 + index);
                if (!questionData[key]) return null;
                return (
                  <div key={key} style={{ display: 'flex', alignItems: 'flex-start', padding: '10px', border: '1px solid #e9ecef', borderRadius: '4px', backgroundColor: '#fff' }}>
                    <span style={{ fontWeight: 'bold', color: '#5856d6', marginRight: '10px', minWidth: '20px' }}>({label})</span>
                    <div style={{ flexGrow: '1' }}>{renderMathText(questionData[key])}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Step-by-Step Mathematical Explanation Solution Section Layout */}
          <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
            <h4 style={{ color: '#198754', marginBottom: '10px', fontSize: '1.15rem' }}>DETAILED ANSWER EXPLANATION / SOLUTION:</h4>
            <div style={{ padding: '15px', backgroundColor: '#f4faf6', borderLeft: '4px solid #198754', borderRadius: '4px', lineHeight: '1.6' }}>
              {questionData.solution ? renderMathText(questionData.solution) : <span style={{ fontStyle: 'italic', color: '#6c757d' }}>No explanation text submitted for this sequence layout.</span>}
            </div>
          </div>

          {/* Document Footer Page Identifier Watermark Layout */}
          <div style={{ marginTop: '40px', paddingTop: '10px', borderTop: '1px solid #e9ecef', textAlign: 'center', fontSize: '0.75rem', color: '#adb5bd' }}>
            This study document is generated automatically via Ambipie Cloud Engine Framework Database Assets. All rights reserved.
          </div>

        </div>
      </div>
    </div>
  );
};

export default DownloadPDF;